import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useNavBadges } from '../hooks/useNavBadges';
import { useGmailBadge } from '../hooks/useGmailBadge';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import {
  House, CalendarCheck, Users, Timer, CalendarDots,
  ChatCircle, Folder, Buildings, Receipt, Wrench, Car,
  CurrencyDollar, Warehouse, AddressBook, HardHat,
  NotePencil, ShieldCheck, Truck, Envelope, QrCode, ChartBar, ClockCounterClockwise,
  NewspaperClipping,
} from '@phosphor-icons/react';
import MobileShell from './layout/MobileShell';
import DesktopShell from './layout/DesktopShell';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Top-level routes where the back button is hidden (kept stable across renders)
const TOP_LEVEL_ROUTES = ['/', '/dashboard', '/login', '/register'];

const adjustColor = (color, amount) => {
  const clamp = (num) => Math.min(255, Math.max(0, num));
  const hex = color.replace('#', '');
  const r = clamp(parseInt(hex.substr(0, 2), 16) + amount);
  const g = clamp(parseInt(hex.substr(2, 2), 16) + amount);
  const b = clamp(parseInt(hex.substr(4, 2), 16) + amount);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

const Layout = ({ children }) => {
  const { user, logout, checkAuth } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [settings, setSettings] = useState({ company_name: 'BATI-BOYS', logo_url: '', primary_color: '#002FA7' });
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const notifRef = useRef(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);
  const [environment, setEnvironment] = useState('production');

  // Detect current environment for the visual badge (preview / staging only)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/system/info`);
        if (!cancelled) setEnvironment((r.data?.environment || 'production').toLowerCase());
      } catch { /* silent — default to production hides the banner */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('sidebarWidth');
    return saved ? parseInt(saved, 10) : 260;
  });
  const [autoCollapse, setAutoCollapse] = useState(() => {
    // On touch-primary devices (iPad landscape, tablets) the collapsed sidebar
    // is unusable because there is no hover to expand it. Default to pinned
    // open unless the user explicitly toggled it off before.
    const saved = localStorage.getItem('sidebarAutoCollapse');
    if (saved !== null) return saved !== 'false';
    const isTouch = typeof window !== 'undefined' && (
      window.matchMedia?.('(hover: none)').matches ||
      window.matchMedia?.('(pointer: coarse)').matches
    );
    return !isTouch;
  });
  const [isHoveringSidebar, setIsHoveringSidebar] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sidebarExpandedGroups') || '{}'); }
    catch { return {}; }
  });
  const toggleGroup = (to) => {
    setExpandedGroups((prev) => {
      const next = { ...prev, [to]: !prev[to] };
      localStorage.setItem('sidebarExpandedGroups', JSON.stringify(next));
      return next;
    });
  };
  const hoverTimerRef = useRef(null);
  const isResizing = useRef(false);
  const sidebarRef = useRef(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [touchOpenFlyout, setTouchOpenFlyout] = useState(null);

  // Close touch-open flyout when clicking outside the sidebar
  useEffect(() => {
    if (!touchOpenFlyout) return undefined;
    const onDocClick = (e) => {
      if (!sidebarRef.current) return;
      if (!sidebarRef.current.contains(e.target) && !e.target.closest('.nav-flyout')) {
        setTouchOpenFlyout(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('touchstart', onDocClick);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('touchstart', onDocClick);
    };
  }, [touchOpenFlyout]);

  // Responsive detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu on navigation
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/notifications/unread-count`, { withCredentials: true });
      setUnreadCount(res.data.count);
    } catch (e) { console.error('Erreur notifications count:', e); }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/notifications`, { withCredentials: true });
      setNotifications(res.data);
    } catch (e) { console.error('Erreur notifications:', e); }
  }, []);

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API_URL}/api/notifications/${id}/read`, {}, { withCredentials: true });
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (e) { console.error('Erreur marquer lu:', e); }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API_URL}/api/notifications/read-all`, {}, { withCredentials: true });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (e) { console.error('Erreur marquer tout lu:', e); }
  };

  const handleNotifClick = (notif) => {
    if (!notif.read) markAsRead(notif.id);
    if (notif.link) {
      navigate(notif.link);
      setShowNotifPanel(false);
    }
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/settings`, { withCredentials: true });
        setSettings(response.data);
        document.documentElement.style.setProperty('--primary', response.data.primary_color);
        document.documentElement.style.setProperty('--primary-raw', response.data.primary_color);
        document.documentElement.style.setProperty('--primary-hover', adjustColor(response.data.primary_color, -20));
        if (response.data.dark_mode) document.documentElement.classList.add('dark');
        else document.documentElement.classList.remove('dark');
      } catch (error) { console.error('Erreur parametres:', error); }
    };
    fetchSettings();
    fetchUnreadCount();
    registerPushSubscription();
  }, [fetchUnreadCount]);

  const registerPushSubscription = async () => {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return;
      const registration = await navigator.serviceWorker.ready;
      const vapidRes = await axios.get(`${API_URL}/api/notifications/vapid-public-key`, { withCredentials: true });
      const vapidKey = vapidRes.data.publicKey;
      if (!vapidKey) return;
      const existing = await registration.pushManager.getSubscription();
      if (existing) {
        await axios.post(`${API_URL}/api/notifications/subscribe`, { subscription: existing.toJSON() }, { withCredentials: true });
        return;
      }
      const padding = '='.repeat((4 - vapidKey.length % 4) % 4);
      const base64 = (vapidKey + padding).replace(/-/g, '+').replace(/_/g, '/');
      const raw = atob(base64);
      const uint8 = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; i++) uint8[i] = raw.charCodeAt(i);
      const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: uint8 });
      await axios.post(`${API_URL}/api/notifications/subscribe`, { subscription: subscription.toJSON() }, { withCredentials: true });
    } catch (e) { console.error('Push subscription error:', e); }
  };

  useEffect(() => {
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifPanel(false);
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) setShowProfileMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sidebar resize (desktop only)
  useEffect(() => {
    if (isMobile) return undefined;
    const handleMouseMove = (e) => {
      if (!isResizing.current) return;
      setSidebarWidth(Math.min(420, Math.max(200, e.clientX)));
    };
    const handleMouseUp = () => {
      if (isResizing.current) {
        isResizing.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        localStorage.setItem('sidebarWidth', String(sidebarWidth));
      }
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [sidebarWidth, isMobile]);

  const startResize = (e) => {
    if (isMobile) return;
    e.preventDefault();
    isResizing.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const employeeLinks = useMemo(() => [
    { to: '/', icon: House, label: 'Accueil' },
    { to: '/my-planning', icon: CalendarDots, label: 'Mon Planning' },
    { to: '/time-entry', icon: Timer, label: t('nav.time_entry') },
    { to: '/dashboard', icon: House, label: t('nav.dashboard') },
    { to: '/my-hours', icon: ClockCounterClockwise, label: t('nav.my_hours') },
    { to: '/finance', icon: CurrencyDollar, label: t('nav.employee_expenses') },
    { to: '/mes-chantiers', icon: Buildings, label: 'Mes chantiers' },
    { to: '/stocks-materiaux', icon: Warehouse, label: 'Stocks' },
    { to: '/employees', icon: Users, label: t('nav.leaves_absences') },
    { to: '/my-documents', icon: Folder, label: t('nav.my_documents') },
    { to: '/my-equipment', icon: Wrench, label: 'Mon materiel' },
    { to: '/scan-materiel', icon: QrCode, label: 'Scan materiel' },
    { to: '/my-vehicle', icon: Car, label: 'Mon vehicule' },
    { to: '/contacts', icon: AddressBook, label: 'Contacts' },
    { to: '/chat', icon: ChatCircle, label: t('nav.chat') },
    { to: '/reports', icon: ChartBar, label: t('nav.reports') },
  ], [t]);

  const adminLinks = useMemo(() => [
    { to: '/', icon: House, label: 'ACCUEIL' },
    { to: '/planning', icon: CalendarDots, label: 'PLANNING' },
    { to: '/validate', icon: ClockCounterClockwise, label: 'VALIDATION HEURES' },
    { to: '/fil-actualite', icon: NewspaperClipping, label: 'FIL D\'ACTUALITÉ' },
    { to: '/notes-taches', icon: NotePencil, label: 'NOTES & TÂCHES' },
    { to: '/correspondance', icon: Envelope, label: 'CORRESPONDANCE' },
    {
      to: '/documents', icon: ShieldCheck, label: 'ADMINISTRATION',
      children: [
        { to: '/documents?tab=salaries_merged', label: 'Salariés' },
        { to: '/documents?tab=conges', label: 'Congés' },
        { to: '/documents?tab=maladie', label: 'Arrêts & absences' },
        { to: '/documents?tab=validate', label: 'Validation heures' },
        { to: '/documents?tab=company_docs', label: 'Docs obligatoires' },
        { to: '/documents?tab=employee_docs', label: 'Docs salariés' },
        { to: '/documents?tab=admin_docs', label: 'Archives' },
        { to: '/reports', label: 'Rapports' },
        { to: '/access', label: 'Accès' },
        { to: '/history', label: 'Historique' },
        { to: '/apercus-interfaces', label: 'Aperçus interfaces' },
      ],
    },
    {
      to: '/projects', icon: Buildings, label: 'CHANTIERS',
      children: [
        { to: '/projects?tab=chantiers', label: 'Chantiers' },
        { to: '/opportunites', label: 'Opportunités' },
        { to: '/programmation', label: 'Programmation' },
        { to: '/projects?tab=facturation', label: 'Facturation' },
        { to: '/projects?tab=courses', label: 'Courses' },
        { to: '/projects?tab=commandes', label: 'Commandes' },
        { to: '/projects?tab=statistiques', label: 'Statistiques' },
        { to: '/projects?tab=bibliotheque_catalogue', label: 'Bibliothèque & Catalogue' },
      ],
    },
    { to: '/sous-traitance', icon: HardHat, label: 'SOUS-TRAITANCE' },
    { to: '/fournisseurs', icon: Truck, label: 'FOURNISSEURS' },
    {
      to: '/finance', icon: Receipt, label: 'FINANCE',
      children: [
        { to: '/finance?tab=depenses', label: 'Dépenses' },
        { to: '/finance?tab=notes_frais', label: 'Notes de frais' },
        { to: '/finance?tab=depenses_societe', label: 'Achats carte société' },
        { to: '/finance?tab=charges', label: 'Charges mensuelles' },
        { to: '/finance?tab=tva', label: 'TVA' },
        { to: '/finance?tab=rapprochement', label: 'Rapprochement bancaire' },
        { to: '/finance?tab=paie', label: 'Variables de paie' },
      ],
    },
    {
      to: '/resources', icon: Warehouse, label: 'RESSOURCES',
      children: [
        { to: '/resources?tab=materiaux_stocks', label: 'Stocks matériaux' },
        { to: '/resources?tab=materiel', label: 'Matériel' },
        { to: '/resources?tab=vehicules', label: 'Véhicules' },
      ],
    },
    { to: '/contacts', icon: AddressBook, label: 'CONTACTS' },
    { to: '/chat', icon: ChatCircle, label: 'CHAT' },
  ], []);

  const links = user?.role === 'admin' ? adminLinks : employeeLinks;
  const { badges: navBadges, markSeen: markNavSeen } = useNavBadges(user?.role === 'admin');
  const gmailUnread = useGmailBadge(user?.role === 'admin');

  // ----- Reorder mode (long-press 3s) -----
  const [reorderMode, setReorderMode] = useState(false);
  const [navOrder, setNavOrder] = useState(() => {
    try {
      const saved = sessionStorage.getItem(`navOrder_${user?.role || 'guest'}`);
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const longPressTimer = useRef(null);
  const dragItemRef = useRef(null);

  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(`navOrder_${user?.role || 'guest'}`);
      setNavOrder(saved ? JSON.parse(saved) : null);
    } catch { setNavOrder(null); }
  }, [user?.role]);

  const orderedLinks = React.useMemo(() => {
    if (!navOrder || navOrder.length === 0) return links;
    const map = new Map(links.map((l) => [l.to, l]));
    const result = [];
    navOrder.forEach((to) => { if (map.has(to)) { result.push(map.get(to)); map.delete(to); } });
    map.forEach((v) => result.push(v));
    return result;
  }, [links, navOrder]);

  const persistOrder = (newOrder) => {
    setNavOrder(newOrder);
    try { sessionStorage.setItem(`navOrder_${user?.role || 'guest'}`, JSON.stringify(newOrder)); } catch { /* noop */ }
  };

  const startLongPress = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    longPressTimer.current = setTimeout(() => {
      setReorderMode(true);
      try { if (navigator.vibrate) navigator.vibrate(50); } catch { /* noop */ }
    }, 2000);
  };
  const cancelLongPress = () => {
    if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
  };

  useEffect(() => {
    if (!reorderMode) return undefined;
    const onClick = (e) => {
      if (!sidebarRef.current) return;
      if (!sidebarRef.current.contains(e.target)) setReorderMode(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setReorderMode(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [reorderMode]);

  const handleDragStart = (e, linkTo) => {
    if (!reorderMode) return;
    dragItemRef.current = linkTo;
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', linkTo); } catch { /* noop */ }
    e.currentTarget.classList.add('nav-dragging');
  };
  const handleDragOver = (e, targetTo) => {
    if (!reorderMode) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const sourceTo = dragItemRef.current;
    if (!sourceTo || !targetTo || sourceTo === targetTo) return;
    const current = orderedLinks.map((l) => l.to);
    const fromIdx = current.indexOf(sourceTo);
    const toIdx = current.indexOf(targetTo);
    if (fromIdx === -1 || toIdx === -1) return;
    const newOrder = [...current];
    const [moved] = newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, moved);
    persistOrder(newOrder);
  };
  const handleDrop = (e) => { if (reorderMode) e.preventDefault(); };
  const handleDragEnd = (e) => {
    e.currentTarget.classList.remove('nav-dragging');
    dragItemRef.current = null;
  };

  // Bottom nav (mobile only)
  const bottomNavLinks = user?.role === 'admin'
    ? [
      { to: '/', icon: House, label: 'Accueil' },
      { to: '/planning', icon: CalendarDots, label: 'Planning' },
      { to: '/projects', icon: Buildings, label: 'Chantiers' },
      { to: '/finance', icon: CurrencyDollar, label: 'Finance' },
    ]
    : [
      { to: '/', icon: House, label: 'Accueil' },
      { to: '/my-planning', icon: CalendarDots, label: 'Planning' },
      { to: '/time-entry', icon: Timer, label: t('nav.time_entry') },
      { to: '/mes-chantiers', icon: Buildings, label: 'Chantiers' },
    ];

  // Back button + swipe-right-to-go-back
  const canGoBack = !TOP_LEVEL_ROUTES.includes(location.pathname);
  const handleBack = useCallback(() => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  }, [navigate]);

  const swipeStartRef = useRef(null);
  useEffect(() => {
    if (!isMobile) return undefined;
    const EDGE_PX = 30;
    const MIN_DX = 80;
    const MAX_DY = 60;

    const onPointerDown = (e) => {
      if (e.clientX > EDGE_PX) return;
      if (e.target?.closest?.('input, textarea, button, select, [role="button"], [contenteditable="true"]')) return;
      swipeStartRef.current = { x: e.clientX, y: e.clientY, t: Date.now() };
    };
    const onPointerUp = (e) => {
      const start = swipeStartRef.current;
      swipeStartRef.current = null;
      if (!start) return;
      const dx = e.clientX - start.x;
      const dy = Math.abs(e.clientY - start.y);
      const dt = Date.now() - start.t;
      if (dt > 700) return;
      if (dx >= MIN_DX && dy <= MAX_DY && canGoBack) handleBack();
    };
    const onPointerCancel = () => { swipeStartRef.current = null; };

    window.addEventListener('pointerdown', onPointerDown, { passive: true });
    window.addEventListener('pointerup', onPointerUp, { passive: true });
    window.addEventListener('pointercancel', onPointerCancel, { passive: true });
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerCancel);
    };
  }, [isMobile, canGoBack, handleBack]);

  // env badge (only in preview / staging)
  const envBadge = environment !== 'production' ? (() => {
    const cfg = environment === 'staging'
      ? { label: 'STAGING', bg: '#7C3AED', text: 'Environnement de pre-production (staging.bati-boys.fr) - les modifications ne sont pas en ligne' }
      : { label: 'PREVIEW', bg: '#DC2626', text: 'Environnement de developpement - non visible en production (bati-boys.fr)' };
    return (
      <div
        data-testid="env-badge-banner"
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, height: '24px',
          background: cfg.bg, color: 'white', display: 'flex', alignItems: 'center',
          justifyContent: 'center', gap: '10px', fontSize: '0.72rem', fontWeight: 700,
          letterSpacing: '0.04em', zIndex: 9999, textTransform: 'uppercase',
          fontFamily: 'Outfit, sans-serif',
        }}
        title={cfg.text}
      >
        <span style={{ background: 'rgba(0,0,0,0.18)', padding: '1px 8px', borderRadius: '4px' }}>{cfg.label}</span>
        <span style={{ opacity: 0.92, textTransform: 'none', fontWeight: 500, letterSpacing: 0 }}>{cfg.text}</span>
      </div>
    );
  })() : null;

  const togglePin = () => {
    const next = !autoCollapse;
    setAutoCollapse(next);
    localStorage.setItem('sidebarAutoCollapse', String(next));
    if (!next) setIsHoveringSidebar(false);
  };

  if (isMobile) {
    return (
      <MobileShell
        envBadge={envBadge}
        environment={environment}
        canGoBack={canGoBack}
        handleBack={handleBack}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        settings={settings}
        notifRef={notifRef}
        notifications={notifications}
        unreadCount={unreadCount}
        showNotifPanel={showNotifPanel}
        setShowNotifPanel={setShowNotifPanel}
        fetchNotifications={fetchNotifications}
        markAllRead={markAllRead}
        handleNotifClick={handleNotifClick}
        t={t}
        user={user}
        links={links}
        navBadges={navBadges}
        gmailUnread={gmailUnread}
        markNavSeen={markNavSeen}
        expandedGroups={expandedGroups}
        toggleGroup={toggleGroup}
        bottomNavLinks={bottomNavLinks}
        location={location}
        navigate={navigate}
        handleLogout={handleLogout}
      >
        {children}
      </MobileShell>
    );
  }

  return (
    <DesktopShell
      envBadge={envBadge}
      environment={environment}
      user={user}
      settings={settings}
      t={t}
      checkAuth={checkAuth}
      location={location}
      navigate={navigate}
      sidebarRef={sidebarRef}
      autoCollapse={autoCollapse}
      togglePin={togglePin}
      isHoveringSidebar={isHoveringSidebar}
      sidebarWidth={sidebarWidth}
      notifRef={notifRef}
      notifications={notifications}
      unreadCount={unreadCount}
      showNotifPanel={showNotifPanel}
      setShowNotifPanel={setShowNotifPanel}
      fetchNotifications={fetchNotifications}
      markAllRead={markAllRead}
      handleNotifClick={handleNotifClick}
      orderedLinks={orderedLinks}
      navBadges={navBadges}
      gmailUnread={gmailUnread}
      markNavSeen={markNavSeen}
      expandedGroups={expandedGroups}
      toggleGroup={toggleGroup}
      reorderMode={reorderMode}
      startLongPress={startLongPress}
      cancelLongPress={cancelLongPress}
      handleDragStart={handleDragStart}
      handleDragOver={handleDragOver}
      handleDrop={handleDrop}
      handleDragEnd={handleDragEnd}
      touchOpenFlyout={touchOpenFlyout}
      setTouchOpenFlyout={setTouchOpenFlyout}
      profileMenuRef={profileMenuRef}
      showProfileMenu={showProfileMenu}
      setShowProfileMenu={setShowProfileMenu}
      handleLogout={handleLogout}
      isResizing={isResizing}
      startResize={startResize}
    >
      {children}
    </DesktopShell>
  );
};

export default Layout;
